import React, { useRef, useEffect } from "react";
import { observer } from "mobx-react";
import { visualizationStore } from "../../stores/VisualizationStore";

const SNAP_POINTS = [50, 60, 70];     // canvas percentages: 50/50, 40/60, 30/70 chat:canvas
const SNAP_TOLERANCE = 2;              // ±2% per snap; total 12% dead zone of [25,75]
const MIN_PCT = 25;
const MAX_PCT = 75;
const DEFAULT_PCT = 60;               // double-click reset target (parity with the rail resizer)
const KEYBOARD_NUDGE = 5;

export function computeWidthFromPointer(clientX, viewportWidth) {
  const raw = ((viewportWidth - clientX) / viewportWidth) * 100;
  return Math.max(MIN_PCT, Math.min(MAX_PCT, raw));
}

export function applySnapZones(pct) {
  for (const snap of SNAP_POINTS) {
    if (Math.abs(pct - snap) <= SNAP_TOLERANCE) return snap;
  }
  return pct;
}

const writeLiveCssVar = (pct) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--viz-canvas-w', `${pct}vw`);
};

export const CanvasResizer = observer(() => {
  const handleRef = useRef(null);
  const dragRef = useRef({ active: false, latestPct: visualizationStore.canvasWidthPercent });

  useEffect(() => {
    dragRef.current.latestPct = visualizationStore.canvasWidthPercent;
    writeLiveCssVar(visualizationStore.canvasWidthPercent);
  }, [visualizationStore.canvasWidthPercent]);

  useEffect(() => {
    // Reset body styles on unmount in case unmount happens during a drag.
    return () => {
      if (dragRef.current.active) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.classList.remove('viz-canvas-dragging');
        dragRef.current.active = false;
      }
    };
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    handleRef.current?.setPointerCapture?.(e.pointerId);
    dragRef.current.active = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.classList.add('viz-canvas-dragging');
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const raw = computeWidthFromPointer(e.clientX, window.innerWidth);
    const snapped = applySnapZones(raw);
    dragRef.current.latestPct = snapped;
    writeLiveCssVar(snapped);
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    handleRef.current?.releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.classList.remove('viz-canvas-dragging');
    visualizationStore.setCanvasWidthPercent(dragRef.current.latestPct);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      visualizationStore.setCanvasWidthPercent(visualizationStore.canvasWidthPercent + KEYBOARD_NUDGE);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      visualizationStore.setCanvasWidthPercent(visualizationStore.canvasWidthPercent - KEYBOARD_NUDGE);
    } else if (e.key === 'Home') {
      e.preventDefault();
      visualizationStore.setCanvasWidthPercent(MIN_PCT);
    } else if (e.key === 'End') {
      e.preventDefault();
      visualizationStore.setCanvasWidthPercent(MAX_PCT);
    }
  };

  // Double-click resets to the default split (parity with the rail resizer).
  const onDoubleClick = () => visualizationStore.setCanvasWidthPercent(DEFAULT_PCT);

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={visualizationStore.canvasWidthPercent}
      aria-valuemin={MIN_PCT}
      aria-valuemax={MAX_PCT}
      tabIndex={0}
      className="canvas-resizer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      onDoubleClick={onDoubleClick}
      style={{ right: `var(--viz-canvas-w, 0vw)` }}
    />
  );
});
